import { forwardRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { breakpoints, Column, Columns, Inline } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { css } from '@eti/styles';
import { useAuth } from '@eti/authentication';
import { useTranslation } from '@eti/providers';
import TripDetailsTitleWrapper from '../../pages/result/components/TripDetailsTitleWrapper/TripDetailsTitleWrapper';
import MyBookingsButton from '../../common/authentication/components/MyBookingsButton';
import MyBookingsSkeleton from '../../common/authentication/components/MyBookingsSkeleton';
import LanguageSelectionButton from '../../common/localization/components/LanguageSelectionButton';
import SettingsGraphqlContainer from '../../common/settings/components/SettingsGraphqlContainer';
import SupportMenu from './site_menu/SupportMenu';
import SiteLogo from './SiteLogo';

const contentWrapperStyles = css`
  align-items: center;
  display: flex;
  padding: 8px;
`;

const columnsWrapperStyles = css`
  align-items: center;
`;

interface SiteHeaderV2Props {
  baseUrl: string;
  brand: string;
  isSettingsEnabled?: boolean;
  shouldShowMultiLanguage: boolean;
  shouldShowSupportMenu: boolean;
  siteName: string;
  supportMenu: any;
}

export const SiteHeaderV2 = forwardRef<HTMLElement, SiteHeaderV2Props>(
  (
    {
      siteName,
      baseUrl,
      brand,
      shouldShowSupportMenu,
      shouldShowMultiLanguage,
      supportMenu,
      isSettingsEnabled = false,
    },
    ref,
  ) => {
    const { user, isVerifyingUser } = useAuth();
    const { t } = useTranslation();
    const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);

    return (
      <ContentWrapper className={contentWrapperStyles}>
        <Columns
          className={columnsWrapperStyles}
          spacing={{
            [breakpoints._0]: 16,
            [breakpoints._970]: 24,
          }}
        >
          <Column data-testid="siteHeader" width={{ [breakpoints._0]: '3/12' }}>
            <SiteLogo baseUrl={baseUrl} brand={brand} ref={ref} siteName={siteName} />
          </Column>

          <Column
            data-testid="trip-details-search-bar"
            width={{ [breakpoints._0]: '9/12', [breakpoints._768]: '8/12' }}
          >
            <TripDetailsTitleWrapper />
          </Column>
          {isWiderViewport && (
            <Column
              data-testid="language-and-bookings-button-container"
              width={{ [breakpoints._768]: '1/12' }}
            >
              <nav aria-label={t('ScreenReader.SiteHeader.AccountAndSupport.Navigation.AriaLabel')}>
                <Inline align="end" alignY="center" noWrap spacing={8}>
                  {isSettingsEnabled ? (
                    <SettingsGraphqlContainer componentType="Header" iconOnly />
                  ) : (
                    <>
                      {shouldShowMultiLanguage && !isSettingsEnabled && <LanguageSelectionButton />}
                    </>
                  )}
                  {shouldShowSupportMenu && <SupportMenu iconOnly menu={supportMenu} />}
                  {isVerifyingUser ? (
                    <MyBookingsSkeleton />
                  ) : (
                    user && <MyBookingsButton shouldHideLabel />
                  )}
                </Inline>
              </nav>
            </Column>
          )}
        </Columns>
      </ContentWrapper>
    );
  },
);
